import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Flame,
    LayoutGrid,
    ChevronRight,
    ChevronLeft,
    X,
    Heart,
    Plus,
    ChefHat,
    Pause,
    Gift,
    Sparkles,
} from "lucide-react";

const STEPS = [
    {
        id: "welcome",
        kicker: "Welcome",
        title: "Subscribe. Train. Eat.",
        body:
            "FlexFeed is a TikTok-style marketplace where elite trainers share workout plans you can subscribe to — and pair them with chef-curated recipes.",
        image:
            "https://static.prod-images.emergentagent.com/jobs/1f22cf28-b917-4140-9ab6-3cd1851d807d/images/fa0e6546bdef8c63c45ce9948cae755f0c7feabb511010e8f6bb2ecf2a263b5c.png",
        chips: ["Subscriptions", "Free recipes", "No meal shipping"],
        Icon: Flame,
    },
    {
        id: "feed",
        kicker: "Step 1",
        title: "Swipe the feed",
        body:
            "Workouts and recipe cards stack vertically. Swipe up to discover. Tap the heart, save your favorites, share to a friend.",
        image:
            "https://static.prod-images.emergentagent.com/jobs/1f22cf28-b917-4140-9ab6-3cd1851d807d/images/f47d08716f62c3e9029aa72f09753bd24a77a6e06c8768d7f22dcdb7dbc85ccf.png",
        chips: ["Vertical feed", "Like / save / share", "For-you mix"],
        Icon: LayoutGrid,
    },
    {
        id: "subscribe",
        kicker: "Step 2",
        title: "Pick a plan",
        body:
            "Tap a workout you like. Each plan offers Weekly, Monthly, or Quarterly tiers — the Most Popular badge marks the best value. Tap Subscribe and pay with Stripe.",
        image:
            "https://images.unsplash.com/photo-1599058917212-d750089bc07e",
        chips: ["3 tier options", "Stripe checkout", "Pause anytime"],
        Icon: Plus,
    },
    {
        id: "recipes",
        kicker: "Step 3",
        title: "Recipes pair with workouts",
        body:
            "Every workout subscription unlocks chef-curated recipe suggestions — full ingredients, steps, and macros. They're meal IDEAS, not shipped meals.",
        image:
            "https://static.prod-images.emergentagent.com/jobs/1f22cf28-b917-4140-9ab6-3cd1851d807d/images/956b71ab3222aaaa2a1d8fe6dbf9741c3a4012548cbb75bce13e0cb1597e869a.png",
        chips: ["Free with sub", "Macros + steps", "Cook at home"],
        Icon: ChefHat,
    },
    {
        id: "manage",
        kicker: "Step 4",
        title: "Manage in My Subs",
        body:
            "Pause, resume, or cancel anytime — no questions. Refer a friend with your code (FXxxxxxx) and earn a free week on any subscription.",
        image:
            "https://images.unsplash.com/photo-1517836357463-d25dfeac3438",
        chips: ["Pause / resume", "Cancel anytime", "Free weeks via referral"],
        Icon: Sparkles,
    },
];

const DemoPage = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const touchStart = useRef(null);

    const goNext = () => setStep((s) => Math.min(STEPS.length - 1, s + 1));
    const goPrev = () => setStep((s) => Math.max(0, s - 1));

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "ArrowRight") goNext();
            if (e.key === "ArrowLeft") goPrev();
            if (e.key === "Escape") navigate("/");
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [navigate]);

    const onTouchStart = (e) => {
        touchStart.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (touchStart.current == null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (dx < -50) goNext();
        else if (dx > 50) goPrev();
        touchStart.current = null;
    };

    const cur = STEPS[step];
    const isLast = step === STEPS.length - 1;
    const Icon = cur.Icon;

    return (
        <div
            data-testid="demo-page"
            className="min-h-[100dvh] bg-[#0A0A0A] text-white relative overflow-hidden"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {/* Background image with crossfade */}
            <div className="absolute inset-0">
                {STEPS.map((s, i) => (
                    <img
                        key={s.id}
                        src={s.image}
                        alt=""
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                            i === step ? "opacity-50" : "opacity-0"
                        }`}
                    />
                ))}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/40" />
            </div>

            {/* Progress dots */}
            <div className="relative z-10 flex items-center justify-between px-5 pt-6">
                <span className="font-display text-2xl tracking-wider">FLEXFEED</span>
                <button
                    data-testid="demo-skip"
                    onClick={() => navigate("/")}
                    className="text-[10px] uppercase tracking-widest text-white/60 hover:text-white flex items-center gap-1"
                >
                    Skip <X size={12} />
                </button>
            </div>

            <div className="relative z-10 flex items-center gap-1.5 px-5 mt-3">
                {STEPS.map((_, i) => (
                    <button
                        key={i}
                        data-testid={`demo-dot-${i}`}
                        onClick={() => setStep(i)}
                        className={`h-1 flex-1 rounded-full transition-all duration-500 ${
                            i === step
                                ? "bg-[#E1FF01]"
                                : i < step
                                ? "bg-white/60"
                                : "bg-white/15"
                        }`}
                    />
                ))}
            </div>

            {/* Content */}
            <div className="relative z-10 flex flex-col justify-end min-h-[100dvh] px-6 pb-32 pt-24">
                <div key={cur.id} className="animate-fade-up">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="w-10 h-10 rounded-full bg-[#E1FF01] flex items-center justify-center">
                            <Icon size={18} className="text-[#0A0A0A]" strokeWidth={2.5} />
                        </span>
                        <p className="text-xs uppercase tracking-[0.3em] text-[#E1FF01]">
                            {cur.kicker}
                        </p>
                    </div>

                    <h1 className="font-display text-5xl sm:text-6xl uppercase leading-[0.95] mb-4">
                        {cur.title}
                    </h1>
                    <p className="text-sm text-white/80 max-w-md leading-relaxed mb-5">
                        {cur.body}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-2">
                        {cur.chips.map((c) => (
                            <span
                                key={c}
                                className="text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/10 border border-white/15"
                            >
                                {c}
                            </span>
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom controls */}
            <div className="absolute inset-x-0 bottom-0 z-20 px-5 pb-7">
                <div className="flex items-center gap-3">
                    <button
                        data-testid="demo-prev"
                        onClick={goPrev}
                        disabled={step === 0}
                        className="w-12 h-12 rounded-full bg-white/10 border border-white/15 flex items-center justify-center disabled:opacity-30 active:scale-95 transition"
                    >
                        <ChevronLeft size={20} />
                    </button>

                    {isLast ? (
                        <div className="flex-1 flex gap-2">
                            <button
                                data-testid="demo-cta-browse"
                                onClick={() => navigate("/browse")}
                                className="flex-1 bg-white/10 border border-white/15 text-white font-display text-base uppercase tracking-wider py-3.5 rounded-md active:scale-[0.98] transition"
                            >
                                Browse
                            </button>
                            <button
                                data-testid="demo-cta-signup"
                                onClick={() => navigate("/login")}
                                className="flex-[1.4] bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] font-display text-lg uppercase tracking-wider py-3.5 rounded-md flex items-center justify-center gap-1.5 active:scale-[0.98] transition shadow-[0_8px_24px_rgba(225,255,1,0.25)]"
                            >
                                Sign up free
                                <ChevronRight size={18} strokeWidth={3} />
                            </button>
                        </div>
                    ) : (
                        <button
                            data-testid="demo-next"
                            onClick={goNext}
                            className="flex-1 bg-[#E1FF01] hover:bg-[#CDE800] text-[#0A0A0A] font-display text-lg uppercase tracking-wider py-3.5 rounded-md flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
                        >
                            Next <ChevronRight size={18} strokeWidth={3} />
                        </button>
                    )}
                </div>
                <p className="text-[10px] uppercase tracking-widest text-white/30 text-center mt-3">
                    {step + 1} / {STEPS.length} · Swipe or tap arrows
                </p>
            </div>
        </div>
    );
};

export default DemoPage;
